/**
 * Functions to open popup windows
 * 
 * Popups receive only popupId in URL. All data is requested via BroadcastChannel
 * after the popup opens.
 */

import { PopupType, PopupConfig, POPUP_CONFIGS } from './types';
import { getOtherScreen } from './screenUtils';

/**
 * Generate a unique popup ID
 */
export const generatePopupId = (type: PopupType): string => {
  return `${type}_${Date.now()}`;
};

/**
 * Build window features string for popup
 */
const buildWindowFeatures = (
  config: PopupConfig,
  position?: { left: number; top: number }
): string => {
  const features = [
    `width=${config.width}`,
    `height=${config.height}`,
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ];

  if (position) {
    features.push(`left=${position.left}`, `top=${position.top}`);
  }

  if (config.features) {
    features.push(config.features);
  }

  return features.join(',');
};

/**
 * Build popup URL with params
 */
const buildPopupUrl = (
  type: PopupType,
  popupId: string,
  params: Record<string, string | number>
): string => {
  const urlParams = new URLSearchParams({
    popupId,
    ...Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    ),
  });
  return `#/popup/${type}?${urlParams}`;
};

/**
 * Open a popup window
 * 
 * @param type - The type of popup to open
 * @param params - URL params to pass (typically just identifiers like jobId)
 * @param configOverrides - Optional window size overrides
 * @returns Object with popup window reference and popupId
 * 
 * @example
 * const { popupId, popup } = openPopup(PopupType.Report, { jobId: '123' });
 * // Main window should listen for POPUP_INIT and respond with data
 */
export const openPopup = (
  type: PopupType,
  params: Record<string, string | number> = {},
  configOverrides?: Partial<PopupConfig>
): { popupId: string; popup: Window | null } => {
  const config = { ...POPUP_CONFIGS[type], ...configOverrides };
  const popupId = generatePopupId(type);
  const url = buildPopupUrl(type, popupId, params);
  const features = buildWindowFeatures(config);
  const popup = window.open(url, popupId, features);

  return { popupId, popup };
};

/**
 * Open a popup window on a secondary screen if available.
 * Falls back to primary screen if:
 * - No secondary screen available
 * - User denies permission
 * - Window Management API not supported
 * 
 * @param type - The type of popup to open
 * @param params - URL params to pass (typically just identifiers like jobId)
 * @param configOverrides - Optional window size overrides
 * @returns Promise with popup window reference and popupId
 */
export const openPopupOnSecondaryScreen = async (
  type: PopupType,
  params: Record<string, string | number> = {},
  configOverrides?: Partial<PopupConfig>
): Promise<{ popupId: string; popup: Window | null }> => {
  const config = { ...POPUP_CONFIGS[type], ...configOverrides };
  const popupId = generatePopupId(type);
  const url = buildPopupUrl(type, popupId, params);

  // Try to get a different screen from the current one (won't prompt if not available)
  const otherScreen = await getOtherScreen();

  // If we have another screen, maximize to fill it; otherwise use default config
  const effectiveConfig = otherScreen
    ? { ...config, width: otherScreen.availWidth, height: otherScreen.availHeight }
    : config;

  const position = otherScreen
    ? { left: otherScreen.availLeft, top: otherScreen.availTop }
    : undefined;

  const features = buildWindowFeatures(effectiveConfig, position);
  const popup = window.open(url, popupId, features);

  return { popupId, popup };
};
